import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface HeroSectionProps {
  desktopImage: string;
  mobileImage: string;
  title: React.ReactNode;
  subtitle?: string;
  tagline?: string;
  children?: React.ReactNode;
  className?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  desktopImage,
  mobileImage,
  title,
  subtitle,
  tagline,
  children,
  className = ''
}) => {
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check on mount
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get current image based on device type
  const currentImage = isMobile ? mobileImage : desktopImage;

  // Preload images for better performance
  useEffect(() => {
    const img = new Image();
    img.src = currentImage;
    
    // Preload the other image for faster switching
    const otherImage = isMobile ? desktopImage : mobileImage;
    const otherImg = new Image();
    otherImg.src = otherImage;
  }, [currentImage, desktopImage, mobileImage, isMobile]);

  return (
    <section className={`relative h-screen overflow-hidden ${className}`}>
      {/* Dynamic Background */}
      <div className="absolute inset-0">
        <motion.div 
          className="w-full h-full absolute inset-0"
          key={`${isMobile ? 'mobile' : 'desktop'}-hero`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ 
            duration: 0.8, 
            ease: "easeInOut"
          }}
        >
          <div 
            className="w-full h-full hero-image"
            style={{
              backgroundImage: `url(${currentImage})`,
              backgroundSize: 'cover',
              backgroundPosition: isMobile ? 'center center' : 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        </motion.div>
        
        {/* Smart mobile overlay - lighter and more strategic */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-transparent sm:bg-black/40 z-10"></div>
        
        {/* Additional text-area specific overlay for mobile */}
        <div className="absolute inset-0 bg-gradient-radial-mobile sm:hidden z-10"></div>
      </div>
      
      <div className="relative z-20 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div 
              className="glass-effect-mobile sm:glass-effect rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <motion.h1 
                className="text-4xl sm:text-5xl md:text-7xl font-light mb-4 sm:mb-6 mobile-hero-text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
              >
                {title}
              </motion.h1>
              
              {subtitle && (
                <motion.p 
                  className="text-lg sm:text-xl md:text-2xl text-secondary/90 mb-6 sm:mb-8 leading-relaxed max-w-4xl mx-auto mobile-hero-subtitle"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.5 }}
                >
                  {subtitle}
                </motion.p>
              )}
              
              {children}
              
              {tagline && (
                <motion.div 
                  className="text-highlight/90 text-xs sm:text-sm uppercase tracking-wider font-medium mobile-hero-tagline mt-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.7 }}
                >
                  {tagline}
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection; 